<template>
  <div class="property-default">
    <!-- Os três modos, com a cara dos pares do Comportamento -->
    <ontos-choice-toggle :model-value="mode" :label="$tt('default-value')" :options="modes"
                         @update:model-value="choose($event)"/>
    <!-- O MESMO campo do formulário do item (pedido do usuário): quem configura escolhe o padrão como o
         usuário vai escolher o valor. Sem obrigatório, de propósito: lá ele tira o X, com a regra
         "obrigatório não se limpa"; aqui obrigatório é o valor do ITEM, e não do padrão. A coleção vai
         junto: no markdown, o `@` oferece os campos dela (pedido do usuário) — o `{{campo}}` do padrão é
         do item que vai nascer com ele -->
    <ontos-instance-property-input v-if="mode === 'fixed'" class="property-default-field"
                                   :class="{'property-default-field--wide': wide}"
                                   :property="field" :value="fixedValue" :domain="candidates" :options="options"
                                   :system-alias="systemAlias" :idea-alias="ideaAlias"
                                   @input="setFixed($event)" @change="setFixed($event)"/>
    <ontos-formula-input v-else-if="mode === 'formula'" ref="editor" class="required property-default-formula"
                         v-bind="bindings" :label="$tt('default-mode.formula')" :value="formula" default-formula
                         :draft="draft" :result-label="resultLabel" :example="example"
                         @input="$emit('input', {formula: $event})"/>
  </div>
</template>

<script lang="ts">
import {Component, Prop, Vue, Watch, toNative} from 'vue-facing-decorator'
import {defineAsyncComponent} from 'vue'
import IdeaProperty, {PropertyDefault} from '@/types/IdeaProperty'
import Idea from '@/types/Idea'
import {DomainOption} from '@/types/Domain'
import AsyncFormulaInput from '@/components/design/property/asyncFormulaInput'
import {editorsValid} from '@/components/design/property/editors'
import ChoiceToggle, {ChoiceOption} from '@/components/shared/ChoiceToggle.vue'
import {collectionReadConfig} from '@/utils/collectionRead'
import {displayKind, isEmpty} from '@/utils/propertyDisplay'

type Mode = 'none' | 'fixed' | 'formula'

/**
 * O padrão como se grava: nenhum quando não há — o fixo sem valor e a fórmula em branco também não são
 * padrão. Um dono para o atributo e o relacionamento, que gravam o mesmo campo.
 */
export function writtenDefault(value: PropertyDefault | null | undefined): PropertyDefault | undefined {
  if (!value) return undefined
  if ('formula' in value) return value.formula.trim() ? value : undefined
  return isEmpty(value.value) ? undefined : value
}

/**
 * O valor padrão de um campo (pedido do usuário): nenhum, fixo — escolhido no mesmo campo do formulário
 * do item — ou por fórmula, que o servidor avalia ao criar e refaz enquanto ninguém mexe no campo.
 *
 * O fixo acompanha a forma do campo: trocar o tipo, o múltiplo, o domínio ou a coleção de destino o
 * limpa — o valor de antes seria de outra forma (um número num campo de lista), ou o id de outra coisa.
 * A fórmula fica, e o servidor a confere de novo ao salvar. Eram limpezas espalhadas pelo atributo (a
 * troca do domínio, do múltiplo, o rollup); a forma é uma pergunta só, e mora aqui.
 */
@Component({
  name: 'ontos-property-default', emits: ['input'],
  components: {
    'ontos-formula-input': AsyncFormulaInput,
    'ontos-choice-toggle': ChoiceToggle,
    //Assíncrono: o campo do item arrasta os editores de cada tipo, e só quem escolhe Fixo precisa dele
    'ontos-instance-property-input': defineAsyncComponent(() => import('@/components/live/instance/InstancePropertyInput.vue'))
  }
})
class PropertyDefaultInput extends Vue {
  /** Os três modos, como a escolha os mostra. */
  get modes(): ChoiceOption<Mode>[] {
    return (['none', 'fixed', 'formula'] as Mode[]).map(value => ({value, label: this.$tt(`default-mode.${value}`) as string}))
  }

  @Prop({type: Object, default: null}) readonly value!: PropertyDefault | null
  /** A propriedade como está no formulário: o tipo, o múltiplo, o domínio e o destino dizem a forma. */
  @Prop({type: Object, required: true}) readonly draft!: Partial<IdeaProperty>
  /** O que o editor de fórmula recebe igual em todo formulário — ver `PropertyInput.formulaBindings`. */
  @Prop({type: Object, required: true}) readonly bindings!: Record<string, unknown>
  /** As opções que o fixo de uma seleção oferece — as do rascunho da lista, quando há (`defaultOptions`). */
  @Prop({type: Array, default: () => []}) readonly options!: DomainOption[]
  /** O tipo do campo escrito por extenso, para o editor dizer que a fórmula dá o tipo certo. */
  @Prop({type: String, default: ''}) readonly resultLabel!: string
  @Prop({type: String, default: ''}) readonly example!: string

  /** O modo escolhido — à parte do valor: Fixo ainda sem valor continua Fixo na tela. */
  mode: Mode = 'none'
  /** Os itens da coleção de destino, para o fixo de um relacionamento. */
  candidates: any[] = []

  created() {
    this.mode = !this.value ? 'none' : 'formula' in this.value ? 'formula' : 'fixed'
    this.loadCandidates()
  }

  get systemAlias(): string {
    return this.bindings.systemAlias as string
  }

  /** A coleção da propriedade — a mesma que o editor de fórmula recebe (`bindings.idea`). */
  get ideaAlias(): string {
    const idea = this.bindings.idea as Idea | undefined
    return idea ? idea.alias : ''
  }

  get formula(): string {
    return this.value && 'formula' in this.value ? this.value.formula : ''
  }

  get fixedValue(): any {
    return this.value && 'value' in this.value ? this.value.value : null
  }

  /** O campo do item para o fixo: a forma do rascunho, sem o que é do VALOR do item (obrigatório, regras). */
  get field(): Partial<IdeaProperty> {
    const {name, alias, type, subtype, multiple, domain, relationship} = this.draft
    return {name: name || this.$tt('default-value') as string, alias: alias || 'default', type, subtype, multiple, domain, relationship}
  }

  /**
   * O fixo de um markdown na largura da coluna, como a fórmula: é um editor com barra, e na de um campo
   * ficava espremido (relato do usuário). No formulário do item, ele também ocupa a linha toda.
   */
  get wide(): boolean {
    return displayKind(this.field as IdeaProperty) === 'markdown'
  }

  /** A forma do valor: mudando ela, o fixo de antes não serve mais. */
  get shape(): string {
    const {type, multiple, domain, relationship} = this.draft
    return JSON.stringify([type, !!multiple, domain, relationship && relationship.idea])
  }

  @Watch('shape')
  onShapeChanged() {
    if (this.mode === 'fixed') this.$emit('input', {value: null})
    this.loadCandidates()
  }

  choose(mode: Mode) {
    this.mode = mode
    this.$emit('input', mode === 'none' ? null : mode === 'fixed' ? {value: null} : {formula: ''})
    this.loadCandidates()
  }

  setFixed(value: any) {
    this.$emit('input', {value})
  }

  async loadCandidates() {
    const target = this.draft.relationship && this.draft.relationship.idea
    if (this.mode !== 'fixed' || !target) return
    try {
      const res = await this.$instances.list({systemAlias: this.systemAlias, ideaAlias: target},
          {...collectionReadConfig(false), properties: []})
      this.candidates = res.value as any[]
    } catch (err) {
      console.warn('Não foi possível ler os itens para o padrão', err)
      this.candidates = []
    }
  }

  /** Antes de salvar: a fórmula confere — e a vazia que ninguém visitou passa a dizer que é obrigatória. */
  validate(): boolean {
    if (this.mode !== 'formula') return true
    return editorsValid(this.$refs.editor) && !!this.formula.trim()
  }
}

export default toNative(PropertyDefaultInput)
</script>

<style scoped>
.property-default {
  margin-top: 12px;
}

/* O campo na largura de um campo, e não da coluna, como o domínio; a fórmula e o markdown, na coluna toda */
.property-default-field {
  margin-top: 12px;
  max-width: 320px;
}

.property-default-field--wide {
  max-width: none;
}

.property-default-formula {
  margin-top: 12px;
}
</style>
